import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, UtensilsCrossed, Users, TrendingUp} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { OrderStatusBadge, Spinner } from "../../components/common/UI";

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState({ orders: 0, restaurants: 0, users: 0, revenue: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [
        { count: orderCount },
        { count: restCount },
        { count: userCount },
        { data: orders },
      ] = await Promise.all([
        supabase.from("orders").select("*", { count: "exact", head: true }),
        supabase.from("restaurants").select("*", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("users").select("*", { count: "exact", head: true }).eq("role", "user"),
        supabase.from("orders").select("*, restaurants(name), users(name, phone)").order("created_at", { ascending: false }).limit(8),
      ]);

      const { data: revenue } = await supabase
        .from("orders")
        .select("total")
        .eq("status", "delivered");
      const totalRevenue = (revenue || []).reduce((s, o) => s + (o.total || 0), 0);

      setStats({ orders: orderCount || 0, restaurants: restCount || 0, users: userCount || 0, revenue: totalRevenue });
      setRecentOrders(orders || []);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6 page-enter">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Welcome back! Here's what's happening.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ShoppingBag} label="Total Orders" value={stats.orders} color="bg-orange-500" sub="All time" />
        <StatCard icon={UtensilsCrossed} label="Restaurants" value={stats.restaurants} color="bg-blue-500" sub="Active" />
        <StatCard icon={Users} label="Customers" value={stats.users} color="bg-purple-500" sub="Registered" />
        <StatCard icon={TrendingUp} label="Revenue" value={`₨${stats.revenue.toLocaleString()}`} color="bg-green-500" sub="From delivered orders" />
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-orange-500 font-semibold hover:text-orange-600">
            View All →
          </Link>
        </div>
        {recentOrders.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No orders yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-6 py-3 text-left">Order</th>
                  <th className="px-6 py-3 text-left">Customer</th>
                  <th className="px-6 py-3 text-left">Restaurant</th>
                  <th className="px-6 py-3 text-left">Total</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 font-mono text-xs text-gray-500">#{order.id.slice(0, 8).toUpperCase()}</td>
                    <td className="px-6 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{order.customer_name}</p>
                        <p className="text-xs text-gray-400">{order.customer_phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-gray-600">{order.restaurants?.name}</td>
                    <td className="px-6 py-3 font-semibold text-gray-900">₨{order.total?.toLocaleString()}</td>
                    <td className="px-6 py-3"><OrderStatusBadge status={order.status} /></td>
                    <td className="px-6 py-3 text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString("en-PK")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
